"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { signIn } from "next-auth/react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { APP_NAME } from "@/lib/brand";
import { Turnstile } from "@/components/auth/turnstile";
import {
  getRememberedIdentifier,
  rememberIdentifier,
  clearRememberedIdentifier,
} from "@/lib/auth/remembered-identifier";
import { loginErrorMessage, LOGIN_MESSAGE } from "@/lib/auth/login-messages";

export default function LoginPage() {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [token, setToken] = useState("");
  const [error, setError] = useState("");

  // Pre-fill the identifier (email OR username) if it was remembered on a previous
  // successful login (identifier only — the password is never stored). On mount.
  useEffect(() => {
    const saved = getRememberedIdentifier();
    if (saved) {
      setIdentifier(saved);
      setRemember(true);
    }
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    setIsLoading(true);
    try {
      const result = await signIn("credentials", {
        identifier: identifier.trim(),
        password,
        turnstileToken: token,
        redirect: false,
      });

      if (result?.error) {
        // Distinguish bad-cred / lockout / system error (never imply the
        // credentials were wrong for a system error).
        setError(loginErrorMessage(result));
        return;
      }

      // Only on success: honour the checkbox — store whichever identifier was typed
      // (email OR username; never the password) or clear any previously remembered
      // one. No server involvement.
      if (remember) rememberIdentifier(identifier.trim());
      else clearRememberedIdentifier();

      router.push("/dashboard");
      router.refresh();
    } catch {
      // signIn itself threw (e.g. a network failure) — a system error, not a
      // statement about the credentials.
      setError(LOGIN_MESSAGE.SYSTEM);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Card className="w-full">
      <CardHeader className="text-center">
        <CardTitle className="text-xl">Log In</CardTitle>
        <CardDescription>Sign in to your {APP_NAME} account</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="identifier">Email or username</Label>
            <Input
              id="identifier"
              type="text"
              placeholder="jane@company.com or janedoe"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              disabled={isLoading}
              autoFocus
            />
          </div>

          <label className="flex cursor-pointer items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              data-testid="remember-identifier"
              className="size-4 rounded border-input accent-primary"
            />
            Remember Me
          </label>

          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <Turnstile onToken={setToken} />

          {error && (
            <div className="space-y-1">
              <p className="text-sm text-destructive">{error}</p>
              {/* The verify-with-username hint is only relevant to a credential
                  failure — never shown under a lockout or system error, which
                  say nothing about the credentials. */}
              {error === LOGIN_MESSAGE.BAD_CRED && (
                <p className="text-xs text-muted-foreground">
                  Just signed up? Sign in with your username to finish verifying.
                </p>
              )}
            </div>
          )}

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing in...
              </>
            ) : (
              "Log In"
            )}
          </Button>

          <div className="text-center">
            <Link
              href="/forgot-password"
              className="text-sm text-muted-foreground hover:text-primary hover:underline"
            >
              Forgot your password?
            </Link>
          </div>
        </form>
      </CardContent>
      <CardFooter className="flex-col gap-4">
        <div className="w-full border-t" />
        <p className="text-sm text-muted-foreground">
          Don&apos;t have an account?{" "}
          <Link href="/signup" className="text-primary hover:underline">
            Sign up
          </Link>
        </p>
      </CardFooter>
    </Card>
  );
}
